import { useOrganization, useUser } from "@clerk/react";
import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";

import { hasClerkPublishableKey } from "../../cloud/publicConfig";
import { fetchOrgMembers, type OrgMember } from "../../orgMembers";
import { resolveOrganizationLogoUrl } from "../onboarding/MorrowOnboarding.logic";
import { useOnboardingProfile } from "../../onboardingStore";
import {
  buildPersonalIdentity,
  personalIdentityAvatarUrl,
} from "../workspace/morrowMemberAvatarResolve";
import { ensureUserAvatarSeed, useUserProfile } from "../workspace/morrowUserProfileStore";
import { MorrowMemoryPanel } from "./MorrowMemoryPanel";
import { MorrowWorkspacePage } from "../workspace/MorrowWorkspacePage";
import { BrainIcon } from "lucide-react";
import { useI18n } from "~/i18n";
export function MorrowMemoryPage() {
  const navigate = useNavigate();
  const { t } = useI18n();

  useEffect(() => {
    void navigate({ to: "/knowledge", search: { view: "memory" }, replace: true });
  }, [navigate]);

  return (
    <MorrowWorkspacePage
      title={t("memory.title")}
      subtitle={t("memory.subtitle")}
      icon={<BrainIcon />}
    >
      <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
    </MorrowWorkspacePage>
  );
}

export function MorrowPersonalMemoryPanel({
  personName,
  personAvatarSeed,
  personEmail = "",
}: {
  readonly personName: string;
  readonly personAvatarSeed?: string;
  readonly personEmail?: string;
}) {
  const rootLogoUrl = personalIdentityAvatarUrl(personName, {
    ...(personAvatarSeed ? { avatarSeed: personAvatarSeed } : {}),
  });

  return (
    <MorrowMemoryPanel
      scope="personal"
      rootName={personName}
      rootLogoUrl={rootLogoUrl}
      selfEmail={personEmail}
      defaultViewMode="graph"
    />
  );
}

export function MorrowOrgMemoryPanel({
  orgName,
  orgLogoUrl = "",
  members = [],
  selfEmail,
}: {
  readonly orgName: string;
  readonly orgLogoUrl?: string;
  readonly members?: readonly OrgMember[];
  readonly selfEmail?: string | undefined;
}) {
  return (
    <MorrowMemoryPanel
      scope="org"
      rootName={orgName}
      rootLogoUrl={orgLogoUrl}
      members={members}
      {...(selfEmail ? { selfEmail } : {})}
      defaultViewMode="graph"
    />
  );
}

export function ClerkMorrowOrgMemoryPanel({ orgName }: { readonly orgName: string }) {
  const { organization } = useOrganization();
  const { user } = useUser();
  const companyLogoFallback = useOnboardingProfile().companyLogoUrl;
  const [members, setMembers] = useState<readonly OrgMember[]>([]);

  useEffect(() => {
    void fetchOrgMembers().then((rows) => setMembers(rows ?? []));
  }, [organization?.id]);

  return (
    <MorrowOrgMemoryPanel
      orgName={orgName}
      orgLogoUrl={organization ? resolveOrganizationLogoUrl(organization, companyLogoFallback) : ""}
      members={members}
      selfEmail={user?.primaryEmailAddress?.emailAddress ?? undefined}
    />
  );
}

function ClerkMorrowPersonalMemoryPanel() {
  const { user } = useUser();
  const profile = useUserProfile();
  const { t } = useI18n();
  const identity = buildPersonalIdentity({
    user,
    profile,
    fallbackName: t("team.you"),
  });

  useEffect(() => {
    ensureUserAvatarSeed();
  }, []);

  return (
    <MorrowPersonalMemoryPanel
      personName={identity.personName}
      personEmail={identity.personEmail}
      {...(identity.avatar.avatarSeed ? { personAvatarSeed: identity.avatar.avatarSeed } : {})}
    />
  );
}

function LocalMorrowPersonalMemoryPanel() {
  const profile = useUserProfile();
  const { t } = useI18n();
  const identity = buildPersonalIdentity({
    profile,
    fallbackName: t("team.you"),
  });

  useEffect(() => {
    ensureUserAvatarSeed();
  }, []);

  return (
    <MorrowPersonalMemoryPanel
      personName={identity.personName}
      personEmail={identity.personEmail}
      {...(identity.avatar.avatarSeed ? { personAvatarSeed: identity.avatar.avatarSeed } : {})}
    />
  );
}

export function MorrowScopedMemoryPanel({
  scope,
  orgName,
}: {
  readonly scope: "personal" | "org";
  readonly orgName?: string;
}) {
  if (scope === "org") {
    if (hasClerkPublishableKey()) {
      return <ClerkMorrowOrgMemoryPanel orgName={orgName ?? ""} />;
    }
    return <MorrowOrgMemoryPanel orgName={orgName ?? "Company"} />;
  }

  if (hasClerkPublishableKey()) return <ClerkMorrowPersonalMemoryPanel />;
  return <LocalMorrowPersonalMemoryPanel />;
}
